
import React, { useState, useRef, useEffect } from 'react';

// ── BASE URL ─────────────────────────────────────────────────────────────────
const BASE = "https://www.mgmotor.co.in";

// ── DATA — Real MG Motor Icons + Videos ─────────────────────────────────────
const MODES = [
  {
    id: 0,
    label: "Sand",
    desc: "From sand and snow to mud and rock, and every landscape in between, conquering every terrain.",
    iconNormal: `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-icon-005.png`,
    iconActive:  `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-red-icon-005.png`,
    videoDesktop: "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-002",
    videoMobile:  "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-002-portrait",
    poster: "https://mgmotor.scene7.com/is/image/mgmotor/mhubdrivemode-img-002?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
  },
  {
    id: 1,
    label: "Rock",
    desc: "From sand and snow to mud and rock, and every landscape in between, conquering every terrain.",
    iconNormal: `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-icon-001.png`,
    iconActive:  `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-red-icon-001.png`,
    videoDesktop: "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-003",
    videoMobile:  "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-003-portrait",
    poster: "https://mgmotor.scene7.com/is/image/mgmotor/mhubdrivemode-img-003?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
  },
  {
    id: 2,
    label: "Normal",
    desc: "From sand and snow to mud and rock, and every landscape in between, conquering every terrain.",
    iconNormal: `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-icon-002.png`,
    iconActive:  `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-red-icon-002.png`,
    videoDesktop: "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-004",
    videoMobile:  "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-004-portrait",
    poster: "https://mgmotor.scene7.com/is/image/mgmotor/mhubdrivemode-img-004?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
  },
  {
    id: 3,
    label: "Mud",
    desc: "From sand and snow to mud and rock, and every landscape in between, conquering every terrain.",
    iconNormal: `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-icon-003.png`,
    iconActive:  `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-red-icon-003.png`,
    videoDesktop: "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-005",
    videoMobile:  "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-005-portrait",
    poster: "https://mgmotor.scene7.com/is/image/mgmotor/mhubdrivemode-img-005?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
  },
  {
    id: 4,
    label: "Snow",
    desc: "From sand and snow to mud and rock, and every landscape in between, conquering every terrain.",
    iconNormal: `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-icon-004.png`,
    iconActive:  `${BASE}/content/dam/mgmotor/vehicles/mg-majestor/icon/mhubdrivemode-red-icon-004.png`,
    videoDesktop: "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-002",
    videoMobile:  "https://mgmotor.scene7.com/is/content/mgmotor/mhubdrivemode-vid-002-portrait",
    poster: "https://mgmotor.scene7.com/is/image/mgmotor/mhubdrivemode-img-002?fmt=webp-alpha&resMode=bisharp&fit=constrain&qlt=90&wid=1920",
  },
];

const OffRoadModes = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const videoRef = useRef(null);

  const activeMode = MODES[activeIndex];
  const rotationAngle = -(activeIndex * (360 / MODES.length));

  useEffect(() => {
    setFade(false);
    const t = setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.load();
        videoRef.current.play().catch(() => {});
      }
      setFade(true);
    }, 300);
    return () => clearTimeout(t);
  }, [activeIndex]);

  const getIconPosition = (index, total) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    const radius = 112;
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  };

  return (
    <div style={S.container}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@0,700;1,800&family=Barlow:wght@400;500&display=swap');
        .mgbtn { transition: transform 0.3s ease, box-shadow 0.4s ease, background 0.4s ease, border-color 0.4s ease; }
        .mgbtn:hover { transform: translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(1.15) !important; }
        .mgbtn img { transition: opacity 0.3s ease; pointer-events: none; }
      `}</style>

      {/* ── BACKGROUND VIDEO ── */}
      <video
        ref={videoRef}
        key={activeMode.id}
        style={{ ...S.bgVideo, opacity: fade ? 1 : 0 }}
        autoPlay
        muted
        loop
        playsInline
        webkit-playsinline="true"
        preload="metadata"
        poster={activeMode.poster}
      >
        <source src={activeMode.videoDesktop} type="video/mp4" media="(min-width:768px)" />
        <source src={activeMode.videoMobile}  type="video/mp4" media="(min-width:320px)" />
      </video>
      <div style={S.overlay} />

      {/* ── HEADER ── */}
      <div style={S.headerContent}>
        <h4 style={S.poweredBy}>POWERED BY <span style={{ color: '#fff' }}>M-HUB</span></h4>
        <h1 style={S.mainTitle}>10 OFF-ROAD MODES</h1>
        <p style={S.subText}>{activeMode.desc}</p>
      </div>

      {/* ── DIAL + LIST ── */}
      <div style={S.controlsWrapper}>

        <div style={{ ...S.dialContainer, transform: `rotate(${rotationAngle}deg)` }}>

          {/* Center label — always upright */}
          <div style={{ ...S.innerDial, transform: `rotate(${-rotationAngle}deg)` }}>
            <span style={S.modeText}>MODE</span>
          </div>

          {/* Icon Buttons */}
          {MODES.map((mode, i) => {
            const pos = getIconPosition(i, MODES.length);
            const isActive = i === activeIndex;

            // CSS vars for hover scale trick
            const tx = `calc(-50% + ${pos.x}px)`;
            const ty = `calc(-50% + ${pos.y}px)`;
            const rot = `${-rotationAngle}deg`;

            return (
              <button
                key={mode.id}
                className="mgbtn"
                onClick={() => setActiveIndex(i)}
                title={mode.label}
                style={{
                  '--tx': tx,
                  '--ty': ty,
                  '--rot': rot,
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: '56px',
                  height: '56px',
                  transform: `translate(${tx}, ${ty}) rotate(${rot})`,
                  background: isActive ? 'rgba(255,40,40,0.15)' : 'rgba(0,0,0,0.35)',
                  border: isActive
                    ? '1.5px solid rgba(255,60,60,0.85)'
                    : '1.5px solid rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3px',
                  boxShadow: isActive
                    ? '0 0 22px rgba(255,0,0,0.5), inset 0 0 10px rgba(255,0,0,0.08)'
                    : 'none',
                  backdropFilter: 'blur(6px)',
                  padding: '6px',
                }}
              >
                {/* Normal icon (white) — hidden when active */}
                <img
                  src={mode.iconNormal}
                  alt={mode.label}
                  width={28}
                  height={28}
                  style={{
                    position: 'absolute',
                    opacity: isActive ? 0 : 1,
                    width: '28px',
                    height: '28px',
                    objectFit: 'contain',
                  }}
                />
                {/* Active icon (red) — shown when active */}
                <img
                  src={mode.iconActive}
                  alt={mode.label}
                  width={28}
                  height={28}
                  style={{
                    position: 'absolute',
                    opacity: isActive ? 1 : 0,
                    width: '28px',
                    height: '28px',
                    objectFit: 'contain',
                  }}
                />
                {/* Label below icon */}
                <span style={{
                  position: 'absolute',
                  bottom: '6px',
                  fontSize: '5px',
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: '700',
                  color: isActive ? '#ff4444' : 'rgba(255,255,255,0.6)',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                }}>
                  {mode.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Vertical Mode List */}
        <div style={S.modeList}>
          {MODES.map((mode, i) => {
            const isActive = i === activeIndex;
            if (Math.abs(i - activeIndex) > 1) return null;
            return (
              <div
                key={mode.id}
                onClick={() => setActiveIndex(i)}
                style={{
                  ...S.listItem,
                  color: isActive ? '#ff4444' : '#555',
                  fontSize: isActive ? '44px' : '22px',
                  fontStyle: isActive ? 'italic' : 'normal',
                  fontWeight: isActive ? '800' : '400',
                  opacity: isActive ? 1 : 0.45,
                  transform: isActive ? 'translateX(14px)' : 'translateX(0)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                {/* Small icon in list */}
                {isActive && (
                  <img
                    src={mode.iconActive}
                    alt=""
                    style={{ width: '32px', height: '32px', objectFit: 'contain', flexShrink: 0 }}
                  />
                )}
                {mode.label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  container: {
    position: 'relative', width: '100%', height: '100vh',
    overflow: 'hidden', backgroundColor: '#000', color: '#fff',
    fontFamily: "'Barlow', sans-serif",
  },
  bgVideo: {
    position: 'absolute', top: 0, left: 0,
    width: '100%', height: '100%',
    objectFit: 'cover',
    transition: 'opacity 0.7s ease',
    zIndex: 0,
  },
  overlay: {
    position: 'absolute', top: 0, left: 0,
    width: '100%', height: '100%',
    background: 'linear-gradient(90deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.45) 55%, transparent 100%)',
    zIndex: 1,
  },
  headerContent: {
    position: 'absolute', top: '12%', left: '8%',
    zIndex: 2, maxWidth: '600px',
  },
  poweredBy: {
    fontStyle: 'italic', letterSpacing: '2px',
    marginBottom: '5px', fontSize: '13px', color: '#aaa', fontWeight: 400,
  },
  mainTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '72px', fontWeight: '800', fontStyle: 'italic',
    margin: '0 0 20px 0', lineHeight: 0.9,
  },
  subText: { fontSize: '18px', maxWidth: '750px', color: '#ccc', lineHeight: 1.6 },
  controlsWrapper: {
    position: 'absolute', bottom: '8%', left: '5%',
    display: 'flex', alignItems: 'center', gap: '50px', zIndex: 3,
  },
  dialContainer: {
    position: 'relative', width: '300px', height: '300px',
    borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 70%)',
    flexShrink: 0,
  },
  innerDial: {
    width: '120px', height: '120px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #1a1a1a, #0d0d0d)',
    border: '2px solid #2a2a2a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.8s ease',
    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.8)',
    zIndex: 2,
  },
  modeText: { fontWeight: 'bold', letterSpacing: '4px', fontSize: '12px', color: '#666' },
  modeList: {
    display: 'flex', flexDirection: 'column',
    fontFamily: "'Barlow Condensed', sans-serif",
    width: '250px', userSelect: 'none',
  },
  listItem: {
    textTransform: 'uppercase',
    transition: 'all 0.4s ease',
    lineHeight: 1.1,
  },
};

export default OffRoadModes;